import React, { useCallback, useEffect, useState } from 'react';
import { Button, Grid, Typography, TextField, Box, Hidden, IconButton
    , FormControl, InputLabel, Select } from "@mui/material";
import { Search as IconSearch, IndeterminateCheckBox as IconIndeterminateCheckBox
    , CheckBox as IconCheckBox, AddCircle as IconAddCircle } from "@mui/icons-material";
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';

import * as catalogoService from "../../services/catalogoService";
import * as filialService from "../../services/filialService";
import trabajadorService from "../../services/trabajadorService";
import { setValue, setResults, setListCargo, setListEstado } from '../../redux/trabajadorSearchSlice';
import DialogYesNo from "../../components/DialogYesNo";
import DataGridBase from "../../components/common/DataGridBase"
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento del personal: terapeutas, secretarias, personal de limpieza,
// director, coordinador, etc.
const CATALOGO_TIPOS_CARGO = 236;

const Index = () => {
    const { isReadOnly } = useMantenimiento("trabajador");
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const filters = useSelector((state) => state.trabajadorSearch.filters);
    const listCargo = useSelector((state) => state.trabajadorSearch.listCargo);
    const listEstado = useSelector((state) => state.trabajadorSearch.listEstado);
    const [listFiliales, setListFiliales] = useState([]);

    const [openDialogAnnulActive, setOpenDialogAnnulActive] = useState(false)
    const openCloseDialogAnnulActive = () => { setOpenDialogAnnulActive(!openDialogAnnulActive); }
    const [selectId, setSelectId] = useState(null);
    const [selectState, setSelectState] = useState(null);

    const [cargando, setCargando] = useState(true);
    const [results, setResultsList] = useState([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchResults = async (overrideFilters = {}) => {
        setCargando(true);

        const currentFilters = { ...filters, ...overrideFilters };
        const filialFiltro = currentFilters.idFilial !== undefined
            ? currentFilters.idFilial
            : (idFilialUsuario > 0 ? idFilialUsuario : 0);

        const data = await trabajadorService.getsTrabajadorResumenView({
            nombres: currentFilters.nombres == null ? "" : currentFilters.nombres,
            idCargo: currentFilters.idCargo == null ? 0 : currentFilters.idCargo,
            idEstado: currentFilters.idEstado == null ? 0 : currentFilters.idEstado,
            idFilial: filialFiltro,
        })
            .catch((err) => { console.log("Err: ", err) });

        // La vista devuelve el id del trabajador en "id": se normaliza para el grid
        // y para los enlaces.
        setResultsList((data || []).map((row) => ({ ...row, idTrabajador: row.id, id: row.id })));
        dispatch(setResults(data || []));
        setCargando(false);
    }
    const fetchListFiliales = async () => {
        const data = await filialService.getFilialesList().catch(() => []);
        setListFiliales(filialService.soloFilialDelUsuario(data, idFilialUsuario));
        if (idFilialUsuario > 0) {
            dispatch(setValue({ name: "idFilial", value: idFilialUsuario }));
        }
    }
    const fetchListCargo = async () => {
        const data = await catalogoService.getCatalogosByPadre(CATALOGO_TIPOS_CARGO)
            .catch((err) => { console.log("Err: ", err) });

        dispatch(setListCargo(data || []));
    }
    const fetchListEstado = async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => { console.log("Err: ", err) });

        dispatch(setListEstado(data || []));
    }

    const annulTrabajador = async () => {
        await trabajadorService.annullTrabajador(selectId)
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
        fetchResults();
    }
    const activeTrabajador = async () => {
        await trabajadorService.activeTrabajador(selectId)
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
        fetchResults();
    }

    useEffect(() => {
        const filialInicial = idFilialUsuario > 0 ? idFilialUsuario : 0;
        if (idFilialUsuario > 0) {
            dispatch(setValue({ name: "idFilial", value: idFilialUsuario }));
        }
        fetchResults({ idFilial: filialInicial });
        fetchListFiliales();
        fetchListCargo();
        fetchListEstado();
    }, [idFilialUsuario])

    const columns = [
        { field: 'idTrabajador', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'filial', headerName: 'Filial', width: 75, minWidth: 65, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => p.row.codigoFilial || p.row.filial || '' },
        { field: 'nombres', headerName: 'Nombres', width: 220, minWidth: 180, sortable: false, headerAlign: 'center'
            , renderCell: (cellValues) => {
                return <Link to={'/trabajador/edit/' + cellValues.row.idTrabajador} style={{ color: 'inherit' }}>
                    {cellValues.row.nombres}
                </Link>;
            }
        },
        { field: 'cargo', headerName: 'Cargo', width: 150, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'seudonimo', headerName: 'Seudonimo', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fechaIngreso', headerName: 'Fecha Ingreso', width: 120, minWidth: 105, sortable: true, headerAlign: 'center', align: 'center',
            valueGetter: (value, row) => value || row?.fechaIngreso || row?.FechaIngreso || '',
            renderCell: (cellValues) => {
                const val = cellValues.value || cellValues.row?.fechaIngreso || cellValues.row?.FechaIngreso;
                return (val && moment(val).isValid()) ? moment(val).format('DD/MM/YYYY') : '';
            }
        },
        { field: 'estado', headerName: 'Estado', width: 75, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'anular', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const esAnulado = cellValues.row.estado === "Anulado" || cellValues.row.estado === "N" || cellValues.row.idEstado === 4;
                return <IconButton aria-label="anular" size='small' style={{ height: 10 }} align={"center"} disabled={isReadOnly} onClick={() => {
                    setSelectId(cellValues.row.idTrabajador);
                    setSelectState(cellValues.row.estado);
                    setOpenDialogAnnulActive(true);
                }}>
                    { esAnulado ? <IconIndeterminateCheckBox /> : <IconCheckBox /> }
                </IconButton>;
            }
        },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Personal
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={12} sm={2.5} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-filialfiltro" shrink>Filial</InputLabel>
                                    <Select
                                        native
                                        label="Filial"
                                        value={filters.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                                        disabled={idFilialUsuario > 0}
                                        onChange={(event) => {
                                            const val = parseInt(event.target.value) || 0;
                                            dispatch(setValue({ name: "idFilial", value: val }));
                                            fetchResults({ idFilial: val });
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                fetchResults();
                                            }
                                        }}
                                        inputProps={{ name: 'idFilial', id: 'input-filialfiltro' }}
                                        size="small"
                                    >
                                        {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Todas las filiales)"}</option>}
                                        {listFiliales.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3.5} md={4}>
                                <TextField
                                    name="nombres"
                                    label="Nombres"
                                    value={filters.nombres}
                                    fullWidth
                                    onChange={(event) => dispatch(setValue({ name: "nombres", value: event.target.value }))}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2.5} md={2.5}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-cargofiltro" shrink>Cargo</InputLabel>
                                    <Select
                                        native
                                        label="Cargo"
                                        value={filters.idCargo}
                                        onChange={(event) => {
                                            const val = parseInt(event.target.value) || 0;
                                            dispatch(setValue({ name: "idCargo", value: val }));
                                            fetchResults({ idCargo: val });
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                fetchResults();
                                            }
                                        }}
                                        inputProps={{ name: 'idCargo', id: 'input-cargofiltro' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listCargo.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadofiltro" shrink>Estado</InputLabel>
                                    <Select
                                        native
                                        label="Estado"
                                        value={filters.idEstado}
                                        onChange={(event) => {
                                            const val = parseInt(event.target.value) || 0;
                                            dispatch(setValue({ name: "idEstado", value: val }));
                                            fetchResults({ idEstado: val });
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                fetchResults();
                                            }
                                        }}
                                        inputProps={{ name: 'idEstado', id: 'input-estadofiltro' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listEstado.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={1.5} md={1.5} display={"flex"} alignItems={"center"}>
                                <Button variant="outlined" fullWidth size="medium" onClick={() => { fetchResults() }}>
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: () => navigate("/trabajador/edit/0")
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                    <DialogYesNo open={openDialogAnnulActive} openClose={openCloseDialogAnnulActive}
                        setYes={selectState == "Anulado" ? activeTrabajador : annulTrabajador}
                        setNo={() => { return }}
                        titulo={(selectState == "Anulado" ? "Activar" : "Anular") + " Personal"}
                        texto={"Esta seguro de " + (selectState == "Anulado" ? "activar" : "anular") + " al personal?"} >
                    </DialogYesNo>
                </Grid>
            </Grid>
        </div>
    )
}
export default Index